import RAPIER from "@dimforge/rapier3d-compat";
import { decode } from "@msgpack/msgpack";
import * as THREE from "three";
import { GameClock } from "../time/GameClock";
import { AstronomyService } from "../time/AstronomyService";
import { WeatherService, type WeatherCategory, type WeatherState } from "../weather/WeatherService";
import { CitySimulation } from "../simulation/CitySimulation";
import { WorldStreamer, type QualityProfile } from "../world/WorldStreamer";
import { PlayerController } from "../player/PlayerController";
import { CollisionLayer, interactionGroups } from "../physics/layers";
import {
  PALETTE,
  disposeObject,
  disposeObjectIncremental,
  projectPlanarUvs,
  roundedBox,
  standardMaterial,
} from "../render/materials";
import {
  BuildingModelFactory,
  type BuildingModelInput,
  type BuildingRenderStats,
} from "../render/buildings";
import { disposeCityTextures, loadCityTextures, type CityTextures } from "../render/textures";
import { SkySystem, type SkySnapshot } from "../render/SkySystem";
import {
  CityLightingSystem,
  type PreparedLightingTile,
  type RetiredLightingTile,
} from "../render/CityLightingSystem";
import { buildRoadBatches } from "../render/roads";
import {
  VehicleSystem,
  type CarSnapshot,
  type CarSpawn,
  type DrivingSnapshot,
  type VehicleEventSnapshot,
} from "../vehicles/VehicleSystem";
import {
  CIVILIAN_VEHICLE_COLORS,
  trafficAppearanceAt,
  type VehicleType,
} from "../vehicles/vehicleModels";
import {
  WORLD_MAGIC,
  ROAD_SCHEMA_VERSION,
  WORLD_SCHEMA_VERSION,
  decodeFloat32,
  decodeUint32,
  validateManifest,
  type DecodedMapTile,
  type MapTileIndexEntry,
  type WorldManifest,
} from "../world/schema";
import type { CityLightingManifest } from "../world/lighting-schema";
import {
  RoadNetwork,
  createVehicleRoute,
  laneEdgesForRoad,
  roadRecordsFromPacked,
  type RoadRecord,
  type LaneEdge,
  type RoadSegment,
  type VehicleRoute,
} from "../world/RoadNetwork";
import { MapTileLoader } from "../world/MapTileLoader";
import {
  VegetationSystem,
  type PreparedVegetationTile,
  type RetiredVegetationTile,
  type VegetationSnapshot,
} from "../world/VegetationSystem";
import {
  PerformanceMonitor,
  advanceFixedDebt,
  type FramePerformanceSample,
  type PerformanceSnapshot,
  type StreamingPerformanceSample,
} from "./PerformanceMonitor";

export interface GameSystem {
  fixedUpdate(stepSeconds: number): void;
  frameUpdate(alpha: number): void;
  dispose(): void;
}
export type { RoadSegment } from "../world/RoadNetwork";

interface LoadedPopulationRoute {
  tileId: string;
  segment: RoadSegment;
}

interface PedestrianRecord {
  group: THREE.Group;
  rigidBody: RAPIER.RigidBody;
  tileId: string;
  sequence: number;
}

interface LoadedMapTile {
  root: THREE.Group;
  colliders: RAPIER.Collider[];
  roads: RoadRecord[];
  buildingRender: BuildingRenderStats;
}
export interface PreparedMapTile extends LoadedMapTile {
  readonly id: string;
  readonly generation: number;
  readonly lighting: PreparedLightingTile | null;
  readonly vegetation: PreparedVegetationTile;
}

interface RetiredMapTile {
  root: THREE.Group;
  colliders: RAPIER.Collider[];
  lighting: RetiredLightingTile | null;
  vegetation: RetiredVegetationTile | null;
}

interface MapTileGeneration {
  generation: number;
  desiredKey: string;
  controller: AbortController;
  pendingLoads: Set<string>;
  prepared: Map<string, PreparedMapTile>;
}

export interface NavigationSnapshot {
  player: { x: number; z: number };
  headingDegrees: number;
  roads: readonly RoadSegment[];
  cars: CarSnapshot[];
  bus?: { x: number; z: number };
  train?: { x: number; z: number };
}

export interface TrafficLaneSnapshot {
  id: string;
  physicalId: number;
  laneIndex: number;
  laneCount: number;
  direction: "FT" | "TF";
  fromX: number;
  fromZ: number;
  toX: number;
  toZ: number;
}

export interface GameSnapshot {
  player: { x: number; y: number; z: number };
  chunks: string[];
  mapTiles: string[];
  vegetation: VegetationSnapshot;
  activeCount: number;
  proxyCount: number;
  abstractCount: number;
  weather: WeatherState;
  sky: SkySnapshot;
  context: string;
  transit: string;
  vehicles: { bus?: { x: number; z: number }; train?: { x: number; z: number } };
  pedestrians: { x: number; z: number }[];
  errors: string[];
  buildingRender: BuildingRenderStats;
  lighting: {
    artificialLight: number;
    windowSurfaces: number;
    windowEmissionIntensity: number;
    streetLights: number;
    activeLocalLights: number;
    shadowCastingStreetLights: number;
    activeHeadlightSources: number;
    shadowCastingHeadlightSources: number;
    trafficSignals: number;
    signalAspects: { red: number; yellow: number; green: number };
    batches: number;
  };
  cars: CarSnapshot[];
  trafficLanes: TrafficLaneSnapshot[];
  driving: DrivingSnapshot | null;
  nearbyCarId: number | null;
  vehicleEvent: VehicleEventSnapshot | null;
  navigation: NavigationSnapshot;
}

const FIXED_STEP = 1 / 60;
const SIM_STEP = 1 / 30;
const MAX_FRAME_DELTA_SECONDS = 0.1;
const MAX_PHYSICS_STEPS_PER_FRAME = 5;
const MAX_SIMULATION_STEPS_PER_FRAME = 3;
const MAP_TILE_SIZE_M = 512;
const POPULATION_CELL_SIZE_M = 128;
const POPULATION_SPAWN_RADIUS_M = 420;
const POPULATION_RETAIN_RADIUS_M = 560;
const POPULATION_ROUTE_LIMIT = 32;

const LANDMARKS = {
  grandCentral: new THREE.Vector2(-469.41, 3377.7),
  grandCentralPlaza: new THREE.Vector2(-530, 3448),
  grandCentralEntrance: new THREE.Vector2(-515, 3440),
  timesSquare: new THREE.Vector2(-1174.39, 2800.35),
  empireState: new THREE.Vector2(-1175.63, 3861.73),
  pennStation: new THREE.Vector2(-1831.69, 3640.58),
  portAuthority: new THREE.Vector2(-1578.16, 2927.13),
  centralParkRamble: new THREE.Vector2(30.91, 477.04),
  bronxHub: new THREE.Vector2(6154.36, -8730.43),
  brooklynHub: new THREE.Vector2(-1438.11, 10075.8),
  queensHub: new THREE.Vector2(7481.44, 3806.9),
  statenIslandHub: new THREE.Vector2(-8688.06, 15577.34),
} as const;

const DEVELOPMENT_FIXTURES: Record<string, THREE.Vector2> = {
  "ordinary-building": new THREE.Vector2(42, 42),
  "pedestrian-collision": new THREE.Vector2(0, 8),
  "footprint-gap": new THREE.Vector2(-543.13, 3459.4),
  "bus-stop": new THREE.Vector2(-32, 8),
  "rail-platform": new THREE.Vector2(-238, 246),
  "vehicle-crash": new THREE.Vector2(0, 8),
  "vehicle-showcase": new THREE.Vector2(0, 8),
};
const MAP_SPAWNS: Record<string, THREE.Vector2> = {
  "grand-central": LANDMARKS.grandCentralPlaza,
  "central-park": LANDMARKS.centralParkRamble,
  "times-square": LANDMARKS.timesSquare,
  "empire-state": LANDMARKS.empireState,
  "penn-station": LANDMARKS.pennStation,
  "port-authority": LANDMARKS.portAuthority,
  bronx: LANDMARKS.bronxHub,
  brooklyn: LANDMARKS.brooklynHub,
  queens: LANDMARKS.queensHub,
  "staten-island": LANDMARKS.statenIslandHub,
};
export interface PerformanceDriveSetup {
  carId: number;
  targetAxis: "x" | "z";
  targetCoordinate: number;
}

export class Game {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(58, 1, 0.1, 4000);
  readonly physics: RAPIER.World;
  readonly player: PlayerController;
  readonly vehicles: VehicleSystem;
  readonly vegetation: VegetationSystem;
  readonly lighting: CityLightingSystem;
  readonly simulation = new CitySimulation(180);
  readonly streamer = new WorldStreamer("medium");
  readonly weatherService = new WeatherService();
  readonly astronomy = new AstronomyService();
  readonly systems: GameSystem[] = [];
  readonly errors: string[] = [];
  readonly navigationWorker: Worker;
  #weather!: WeatherState;
  #sky!: SkySystem;
  #weatherOverride?: WeatherCategory;
  #raf = 0;
  #lastFrame = performance.now();
  #physicsDebt = 0;
  #simulationDebt = 0;
  #lastWallSecond = -1;
  readonly #performanceMonitor = import.meta.env.PROD ? undefined : new PerformanceMonitor();
  readonly #framePerformanceSample: FramePerformanceSample = {
    rawFrameMs: 0,
    cpuFrameMs: 0,
    fixedPhysicsMs: 0,
    simulationMs: 0,
    animateCityMs: 0,
    wallUpdateMs: 0,
    vehicleFrameMs: 0,
    systemFrameMs: 0,
    lightingFrameMs: 0,
    skyFrameMs: 0,
    renderMs: 0,
    fixedSteps: 0,
    simulationSteps: 0,
    rendererCalls: 0,
    triangles: 0,
    droppedDebtMs: 0,
  };
  #paused = true;
  #disposed = false;
  readonly #reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  #rain?: THREE.Points;
  #interior?: "ordinary" | "grand-central";
  #bus?: THREE.Group;
  #train?: THREE.Group;
  #aboard?: "bus" | "train";
  readonly #spawnId: string;
  readonly #mapCenter: THREE.Vector2;
  readonly #usesStaticPopulation: boolean;
  readonly #dynamicBodies = new Map<THREE.Object3D, RAPIER.RigidBody>();
  #mapManifest?: WorldManifest;
  #textures?: CityTextures;
  readonly #pedestrians: PedestrianRecord[] = [];
  #nextPedestrianSequence = 0;
  #buildingModels?: BuildingModelFactory;
  #ordinaryBuildingStats: BuildingRenderStats = {
    buildings: 0,
    parts: 0,
    meshes: 0,
    roles: { brick: 0, stone: 0, glass: 0, roof: 0, metal: 0 },
  };
  readonly #loadedMapTiles = new Map<string, LoadedMapTile>();
  readonly #mapTileLoader = new MapTileLoader();
  #mapGeneration = 0;
  #desiredMapTileKey = "";
  #stagingMapTiles?: MapTileGeneration;
  readonly #retiredMapTiles: RetiredMapTile[] = [];
  readonly #retirementAbortController = new AbortController();
  #retirementDrain?: Promise<void>;
  #roadNetwork = new RoadNetwork([]);
  #mapReconcile?: Promise<void>;
  #pendingMapCenter?: { x: number; z: number };
  #lastPopulationCell?: string;
  #navigationRoads: RoadSegment[] = [];

  private constructor(
    readonly canvas: HTMLCanvasElement,
    readonly clock: GameClock,
    physics: RAPIER.World,
    navigationWorker: Worker,
    lightingManifest: CityLightingManifest,
  ) {
    this.physics = physics;
    this.navigationWorker = navigationWorker;
    this.lighting = new CityLightingSystem(this.scene, lightingManifest);
    this.#spawnId = import.meta.env.PROD
      ? "grand-central"
      : (new URLSearchParams(location.search).get("spawn") ?? "grand-central");
    const lightingSpawn =
      this.#spawnId === "lighting-street"
        ? lightingManifest.showcases.streetLight
        : this.#spawnId === "lighting-signal"
          ? lightingManifest.showcases.trafficSignal
          : undefined;
    this.#usesStaticPopulation = Object.hasOwn(DEVELOPMENT_FIXTURES, this.#spawnId);
    this.#mapCenter =
      (lightingSpawn ? new THREE.Vector2(lightingSpawn.x, lightingSpawn.z) : undefined) ??
      DEVELOPMENT_FIXTURES[this.#spawnId] ??
      MAP_SPAWNS[this.#spawnId] ??
      (this.#spawnId.startsWith("building:")
        ? DEVELOPMENT_FIXTURES["ordinary-building"]!
        : LANDMARKS.grandCentralPlaza);
    if (!import.meta.env.PROD) {
      const weather = new URLSearchParams(location.search).get("weather") as WeatherCategory | null;
      if (weather && ["clear", "cloud", "rain", "snow", "fog", "storm"].includes(weather))
        this.#weatherOverride = weather;
    }
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: "high-performance",
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.scene.fog = new THREE.FogExp2(0x102225, 0.0045);
    this.player = new PlayerController(this.physics, this.camera);
    this.scene.add(this.player.root);
    this.vegetation = new VegetationSystem(this.physics, this.player.body);
    this.systems.push(this.vegetation);
    this.vehicles = new VehicleSystem(this.physics, this.scene, this.camera, this.player);
    this.#applyProfileShadowBudgets();
  }

  static async create(canvas: HTMLCanvasElement, clock: GameClock): Promise<Game> {
    const probe = document.createElement("canvas").getContext("webgl2");
    if (!probe)
      throw new Error(
        "WebGL2 is unavailable. Voxcity needs a current desktop browser with hardware acceleration.",
      );
    const navigationWorker = new Worker(new URL("../world/navigation.worker.ts", import.meta.url), {
      type: "module",
    });
    const lightingReady = CityLightingSystem.loadManifest();
    const navigationReady = new Promise<void>((resolve, reject) => {
      const timeout = window.setTimeout(
        () => reject(new Error("Navigation runtime did not initialize")),
        15_000,
      );
      navigationWorker.addEventListener(
        "message",
        (event: MessageEvent<{ type: string }>) => {
          if (event.data.type === "ready") {
            clearTimeout(timeout);
            resolve();
          }
        },
        { once: true },
      );
      navigationWorker.addEventListener(
        "error",
        () => reject(new Error("Navigation runtime failed to load")),
        { once: true },
      );
    });
    const rapierReady = (async (): Promise<void> => {
      const originalWarn = console.warn;
      console.warn = (...values: unknown[]): void => {
        if (
          values[0] !==
          "using deprecated parameters for the initialization function; pass a single object instead"
        )
          originalWarn(...values);
      };
      try {
        await RAPIER.init();
      } finally {
        console.warn = originalWarn;
      }
    })();
    let lightingManifest: CityLightingManifest;
    try {
      [, , lightingManifest] = await Promise.all([rapierReady, navigationReady, lightingReady]);
    } catch (error) {
      navigationWorker.terminate();
      throw error;
    }
    const physics = new RAPIER.World({ x: 0, y: -9.81, z: 0 });
    physics.timestep = FIXED_STEP;
    const game = new Game(canvas, clock, physics, navigationWorker, lightingManifest);
    try {
      const maxAnisotropy = game.renderer.capabilities.getMaxAnisotropy();
      const [textureResult, vehicleResult] = await Promise.allSettled([
        loadCityTextures(maxAnisotropy),
        game.vehicles.loadModels(maxAnisotropy),
      ]);
      if (textureResult.status === "fulfilled") {
        game.#textures = textureResult.value;
        game.#buildingModels = new BuildingModelFactory(textureResult.value);
      }
      if (textureResult.status === "rejected") throw textureResult.reason;
      if (vehicleResult.status === "rejected") throw vehicleResult.reason;
      game.#buildWorld();
      await game.#buildCompiledGround();
      await game.#initializeMapTiles();
      game.#resize();
      window.addEventListener("resize", game.#resize);
      document.addEventListener("visibilitychange", game.#visibility);
      game.#tick(performance.now());
      return game;
    } catch (error) {
      game.dispose();
      throw error;
    }
  }

  #buildWorld(): void {
    this.physics.createCollider(
      RAPIER.ColliderDesc.cuboid(30_000, 0.25, 30_000)
        .setTranslation(0, -0.25, 0)
        .setCollisionGroups(interactionGroups(CollisionLayer.WORLD, 0xffff)),
    );

    this.#sky = new SkySystem(this.scene, this.camera);

    this.#buildWeather();
    this.#buildInteriors();
    this.player.body.setTranslation({ x: this.#mapCenter.x, y: 2, z: this.#mapCenter.y }, true);
    this.player.root.position.set(this.#mapCenter.x, 2, this.#mapCenter.y);
    if (this.#spawnId === "rail-platform") {
      this.#interior = "grand-central";
      if (this.#train) this.#train.userData.speed = 0;
    }
    this.streamer.reconcile(this.#mapCenter.x, this.#mapCenter.y);
    const now = this.clock.nowZoned();
    this.simulation.reconcile(
      now.toPlainDate().toString(),
      now.hour,
      this.streamer.profile.pedestrians,
    );
  }

  async #buildCompiledGround(): Promise<void> {
    const response = await fetch("/world/ground.msgpack", { credentials: "same-origin" });
    if (!response.ok)
      throw new Error(`Compiled New York City ground failed to load: ${response.status}`);
    const raw = decode(new Uint8Array(await response.arrayBuffer())) as {
      magic: string;
      schemaVersion: number;
      roadSchemaVersion: number;
      boundaryOffsets: Uint8Array;
      boundaries: Uint8Array;
      roads: Uint8Array;
      roadAttributes: Uint8Array;
      roadIds: Uint8Array;
      parkOffsets: Uint8Array;
      parkBoundaries: Uint8Array;
    };
    if (
      raw.magic !== WORLD_MAGIC ||
      raw.schemaVersion !== WORLD_SCHEMA_VERSION ||
      raw.roadSchemaVersion !== ROAD_SCHEMA_VERSION
    )
      throw new Error("Compiled New York City ground schema is invalid");
    const boundaryOffsets = decodeUint32(raw.boundaryOffsets);
    const boundaries = decodeFloat32(raw.boundaries);
    const roads = decodeFloat32(raw.roads);
    const roadAttributes = raw.roadAttributes;
    const roadIds = decodeUint32(raw.roadIds);
    const parkOffsets = decodeUint32(raw.parkOffsets);
    const parkBoundaries = decodeFloat32(raw.parkBoundaries);
    if (
      boundaries.length % 2 !== 0 ||
      boundaryOffsets.length < 2 ||
      roads.length % 5 !== 0 ||
      roadAttributes.length !== (roads.length / 5) * 7 ||
      roadIds.length !== (roads.length / 5) * 2 ||
      parkOffsets.length < 1
    )
      throw new Error("Compiled New York City ground has an invalid record length");
    this.vegetation.indexParkland(parkOffsets, parkBoundaries, 512);

    const textures = this.#textures;
    if (!textures) throw new Error("City textures are not loaded");
    const waterGeometry = new THREE.PlaneGeometry(60_000, 60_000);
    projectPlanarUvs(waterGeometry);
    const water = new THREE.Mesh(
      waterGeometry,
      standardMaterial(0xffffff, 0.36, 0.08, textures.water),
    );
    water.rotation.x = -Math.PI / 2;
    water.position.y = -0.42;
    water.receiveShadow = true;
    this.scene.add(water);

    const landMaterial = standardMaterial(0xffffff, 0.96, 0.02, textures.land);
    for (let ringIndex = 0; ringIndex < boundaryOffsets.length - 1; ringIndex += 1) {
      const start = (boundaryOffsets[ringIndex] ?? 0) * 2;
      const end = (boundaryOffsets[ringIndex + 1] ?? 0) * 2;
      const island = new THREE.Shape();
      for (let offset = start; offset < end; offset += 2) {
        const x = boundaries[offset] ?? 0;
        const shapeY = -(boundaries[offset + 1] ?? 0);
        if (offset === start) island.moveTo(x, shapeY);
        else island.lineTo(x, shapeY);
      }
      const landGeometry = new THREE.ShapeGeometry(island);
      projectPlanarUvs(landGeometry);
      const land = new THREE.Mesh(landGeometry, landMaterial);
      land.rotation.x = -Math.PI / 2;
      land.position.y = -0.08;
      land.receiveShadow = true;
      this.scene.add(land);
    }

    const parkMaterial = standardMaterial(0xffffff, 0.98, 0.02, textures.park);
    for (let ringIndex = 0; ringIndex < parkOffsets.length - 1; ringIndex += 1) {
      const start = (parkOffsets[ringIndex] ?? 0) * 2;
      const end = (parkOffsets[ringIndex + 1] ?? 0) * 2;
      const shape = new THREE.Shape();
      for (let offset = start; offset < end; offset += 2) {
        const x = parkBoundaries[offset] ?? 0;
        const shapeY = -(parkBoundaries[offset + 1] ?? 0);
        if (offset === start) shape.moveTo(x, shapeY);
        else shape.lineTo(x, shapeY);
      }
      const parkGeometry = new THREE.ShapeGeometry(shape);
      projectPlanarUvs(parkGeometry);
      const park = new THREE.Mesh(parkGeometry, parkMaterial);
      park.rotation.x = -Math.PI / 2;
      park.position.y = 0;
      park.receiveShadow = true;
      this.scene.add(park);
    }

    const localRoads: RoadRecord[] = [];
    for (let offset = 0; offset < roads.length; offset += 5) {
      const centerX = ((roads[offset] ?? 0) + (roads[offset + 2] ?? 0)) / 2;
      const centerZ = ((roads[offset + 1] ?? 0) + (roads[offset + 3] ?? 0)) / 2;
      if (Math.hypot(centerX - this.#mapCenter.x, centerZ - this.#mapCenter.y) > 520) continue;
      const index = offset / 5;
      localRoads.push(
        ...roadRecordsFromPacked(
          roads.subarray(offset, offset + 5),
          roadAttributes.subarray(index * 7, index * 7 + 7),
          roadIds.subarray(index * 2, index * 2 + 2),
          "fixture",
        ),
      );
    }
    if (this.#usesStaticPopulation) {
      if (!["pedestrian-collision", "footprint-gap"].includes(this.#spawnId))
        this.#buildStaticTraffic(localRoads);
      if (!["bus-stop", "footprint-gap"].includes(this.#spawnId))
        this.#buildPedestrians(localRoads);
    } else {
      this.#buildTransitAndTrafficLights(localRoads);
    }
  }

  async #initializeMapTiles(): Promise<void> {
    const response = await fetch("/world/manifest.msgpack", { credentials: "same-origin" });
    if (!response.ok)
      throw new Error(`Compiled New York City manifest failed to load: ${response.status}`);
    const manifest = decode(new Uint8Array(await response.arrayBuffer()));
    validateManifest(manifest);
    this.#mapManifest = manifest;
    await this.#reconcileMapTiles(this.#mapCenter.x, this.#mapCenter.y);
    this.#reconcileAmbientPopulation({ x: this.#mapCenter.x, z: this.#mapCenter.y }, true);
    this.#recordStreaming();
  }

  #recordStreaming(
    sample: Partial<
      Pick<
        StreamingPerformanceSample,
        | "fetchMs"
        | "decodeMs"
        | "buildSliceMs"
        | "commitMs"
        | "disposeSliceMs"
        | "committed"
        | "cancelled"
      >
    > = {},
  ): void {
    this.#performanceMonitor?.recordStreaming({
      fetchMs: sample.fetchMs ?? 0,
      decodeMs: sample.decodeMs ?? 0,
      buildSliceMs: sample.buildSliceMs ?? 0,
      commitMs: sample.commitMs ?? 0,
      disposeSliceMs: sample.disposeSliceMs ?? 0,
      committed: sample.committed ?? false,
      cancelled: sample.cancelled ?? false,
      generation: this.#stagingMapTiles?.generation ?? this.#mapGeneration,
      pendingLoads: this.#stagingMapTiles?.pendingLoads.size ?? 0,
      preparedTiles: this.#stagingMapTiles?.prepared.size ?? 0,
      retiredTiles: this.#retiredMapTiles.length,
      activeMapTiles: this.#loadedMapTiles.size,
    });
  }

  #desiredMapEntries(
    playerX: number,
    playerZ: number,
  ): { entries: MapTileIndexEntry[]; key: string } | undefined {
    const manifest = this.#mapManifest;
    if (!manifest) return undefined;
    const centerX = Math.floor(playerX / manifest.mapTileSizeM);
    const centerZ = Math.floor(playerZ / manifest.mapTileSizeM);
    const indexed = new Map(manifest.mapTileIndex.map((entry) => [entry.id, entry]));
    const entries: MapTileIndexEntry[] = [];
    for (let z = centerZ - 1; z <= centerZ + 1; z += 1) {
      for (let x = centerX - 1; x <= centerX + 1; x += 1) {
        const entry = indexed.get(`${x}_${z}`);
        if (entry) entries.push(entry);
      }
    }
    entries.sort((left, right) => {
      const leftX = (left.x + 0.5) * manifest.mapTileSizeM - playerX;
      const leftZ = (left.z + 0.5) * manifest.mapTileSizeM - playerZ;
      const rightX = (right.x + 0.5) * manifest.mapTileSizeM - playerX;
      const rightZ = (right.z + 0.5) * manifest.mapTileSizeM - playerZ;
      return leftX * leftX + leftZ * leftZ - (rightX * rightX + rightZ * rightZ);
    });
    return {
      entries,
      key: entries
        .map(({ id }) => id)
        .sort()
        .join("|"),
    };
  }

  async #reconcileMapTiles(playerX: number, playerZ: number): Promise<void> {
    const desired = this.#desiredMapEntries(playerX, playerZ);
    if (!desired) return;
    if (desired.key === this.#desiredMapTileKey && !this.#stagingMapTiles) return;
    this.#stagingMapTiles?.controller.abort();
    const generation = ++this.#mapGeneration;
    this.#desiredMapTileKey = desired.key;
    const controller = new AbortController();
    const staging: MapTileGeneration = {
      generation,
      desiredKey: desired.key,
      controller,
      pendingLoads: new Set(),
      prepared: new Map(),
    };
    this.#stagingMapTiles = staging;
    const missing = desired.entries.filter(({ id }) => !this.#loadedMapTiles.has(id));
    const loads = new Map<
      string,
      Promise<{ tile: DecodedMapTile; fetchMs: number; decodeMs: number }>
    >();
    for (const entry of missing) {
      staging.pendingLoads.add(entry.id);
      loads.set(entry.id, this.#mapTileLoader.load(entry, generation, controller.signal));
    }
    this.#recordStreaming();
    try {
      for (const entry of missing) {
        const loaded = await loads.get(entry.id)!;
        staging.pendingLoads.delete(entry.id);
        this.#recordStreaming({ fetchMs: loaded.fetchMs, decodeMs: loaded.decodeMs });
        controller.signal.throwIfAborted();
        const prepared = await this.#prepareMapTile(
          entry,
          loaded.tile,
          generation,
          controller.signal,
        );
        controller.signal.throwIfAborted();
        staging.prepared.set(entry.id, prepared);
        this.#recordStreaming();
      }
      controller.signal.throwIfAborted();
      if (this.#stagingMapTiles !== staging)
        throw new DOMException("Map generation superseded", "AbortError");
      this.#swapPreparedMapTiles(desired.entries, staging, playerX, playerZ);
      this.#stagingMapTiles = undefined;
      this.#recordStreaming();
    } catch (error) {
      controller.abort();
      await Promise.allSettled(loads.values());
      for (const prepared of staging.prepared.values())
        await this.#disposePreparedMapTile(prepared);
      staging.prepared.clear();
      staging.pendingLoads.clear();
      if (this.#stagingMapTiles === staging) this.#stagingMapTiles = undefined;
      this.#recordStreaming({
        cancelled: error instanceof DOMException && error.name === "AbortError",
      });
      throw error;
    }
  }

  #queueMapReconcile(playerX: number, playerZ: number): void {
    this.#pendingMapCenter = { x: playerX, z: playerZ };
    const desired = this.#desiredMapEntries(playerX, playerZ);
    if (desired && this.#stagingMapTiles && this.#stagingMapTiles.desiredKey !== desired.key)
      this.#stagingMapTiles.controller.abort();
    if (this.#mapReconcile) return;
    this.#mapReconcile = (async () => {
      while (this.#pendingMapCenter) {
        const target = this.#pendingMapCenter;
        this.#pendingMapCenter = undefined;
        try {
          await this.#reconcileMapTiles(target.x, target.z);
        } catch (error) {
          const aborted = error instanceof DOMException && error.name === "AbortError";
          if (aborted) {
            if (this.#disposed || !this.#pendingMapCenter) return;
            continue;
          }
          this.errors.push(error instanceof Error ? error.message : "Map tile streaming failed");
          this.#pendingMapCenter = undefined;
          return;
        }
        if (!this.#pendingMapCenter) this.#reconcileAmbientPopulation(target, true);
      }
    })().finally(() => {
      this.#mapReconcile = undefined;
    });
  }
  #refreshNavigationRoads(playerX: number, playerZ: number): void {
    const halfExtent = 110;
    this.#navigationRoads = [...this.#loadedMapTiles.values()]
      .flatMap((tile) => tile.roads.map((road) => road.segment))
      .filter(
        ([fromX, fromZ, toX, toZ]) =>
          Math.max(fromX, toX) >= playerX - halfExtent &&
          Math.min(fromX, toX) <= playerX + halfExtent &&
          Math.max(fromZ, toZ) >= playerZ - halfExtent &&
          Math.min(fromZ, toZ) <= playerZ + halfExtent,
      )
      .sort(
        (left, right) =>
          Math.hypot((left[0] + left[2]) / 2 - playerX, (left[1] + left[3]) / 2 - playerZ) -
          Math.hypot((right[0] + right[2]) / 2 - playerX, (right[1] + right[3]) / 2 - playerZ),
      )
      .slice(0, 512);
  }

  #createMapWorkCheckpoint(signal: AbortSignal, phase: "build" | "dispose"): () => Promise<void> {
    let sliceStarted = performance.now();
    return async (): Promise<void> => {
      signal.throwIfAborted();
      const sliceMs = performance.now() - sliceStarted;
      if (sliceMs < 4) return;
      this.#recordStreaming(
        phase === "build" ? { buildSliceMs: sliceMs } : { disposeSliceMs: sliceMs },
      );
      const scheduler = (
        globalThis as typeof globalThis & {
          scheduler?: { yield(): Promise<void> };
        }
      ).scheduler;
      if (scheduler) await scheduler.yield();
      else {
        const { promise, resolve } = Promise.withResolvers<void>();
        setTimeout(resolve, 0);
        await promise;
      }
      signal.throwIfAborted();
      sliceStarted = performance.now();
    };
  }

  async #prepareMapTile(
    entry: MapTileIndexEntry,
    decoded: DecodedMapTile,
    generation: number,
    signal: AbortSignal,
  ): Promise<PreparedMapTile> {
    const root = new THREE.Group();
    root.name = `map-tile:${entry.id}`;
    root.userData.mapTile = entry.id;
    const colliders: RAPIER.Collider[] = [];
    const buildingModels = this.#buildingModels;
    if (!this.#textures || !buildingModels)
      throw new Error("City building resources are not loaded");
    const checkpoint = this.#createMapWorkCheckpoint(signal, "build");
    const lightingPromise = this.lighting.prepareTile(entry.id, generation, signal);
    let lighting: PreparedLightingTile | null | undefined;
    let vegetation: PreparedVegetationTile | undefined;
    let detachedRoadMaterials: THREE.Material[] = [];
    try {
      const buildingInputs: BuildingModelInput[] = [];
      const buildingCount = decoded.buildings.length / 5;
      for (let buildingIndex = 0; buildingIndex < buildingCount; buildingIndex += 1) {
        signal.throwIfAborted();
        const offset = buildingIndex * 5;
        const x = decoded.buildings[offset] ?? 0;
        const z = decoded.buildings[offset + 1] ?? 0;
        const width = decoded.buildings[offset + 2] ?? 0;
        const depth = decoded.buildings[offset + 3] ?? 0;
        const height = decoded.buildings[offset + 4] ?? 0;
        if (width >= 1 && depth >= 1 && height >= 2.7) {
          const footprintStart = decoded.footprintOffsets[buildingIndex] ?? 0;
          const footprintEnd = decoded.footprintOffsets[buildingIndex + 1] ?? footprintStart;
          buildingInputs.push({
            x,
            z,
            width,
            depth,
            height,
            footprint: decoded.footprints,
            footprintStart,
            footprintEnd,
          });
          if (this.#spawnId !== "vehicle-crash") {
            let collider: RAPIER.Collider;
            if (footprintEnd - footprintStart >= 3) {
              const shape = new THREE.Shape();
              for (let pointIndex = footprintStart; pointIndex < footprintEnd; pointIndex += 1) {
                const footprintX = decoded.footprints[pointIndex * 2] ?? x;
                const shapeY = -(decoded.footprints[pointIndex * 2 + 1] ?? z);
                if (pointIndex === footprintStart) shape.moveTo(footprintX, shapeY);
                else shape.lineTo(footprintX, shapeY);
              }
              const collisionGeometry = new THREE.ExtrudeGeometry(shape, {
                depth: height,
                bevelEnabled: true,
                bevelSegments: 1,
                bevelSize: 0.16,
                bevelThickness: 0.16,
              });
              try {
                collisionGeometry.rotateX(-Math.PI / 2);
                const position = collisionGeometry.getAttribute("position");
                const vertices = new Float32Array(position.array);
                const geometryIndex = collisionGeometry.getIndex();
                const indices = geometryIndex
                  ? new Uint32Array(geometryIndex.array)
                  : Uint32Array.from({ length: position.count }, (_, index) => index);
                collider = this.physics.createCollider(
                  RAPIER.ColliderDesc.trimesh(vertices, indices).setCollisionGroups(
                    interactionGroups(CollisionLayer.WORLD, 0xffff),
                  ),
                );
              } finally {
                collisionGeometry.dispose();
              }
            } else {
              collider = this.physics.createCollider(
                RAPIER.ColliderDesc.cuboid(width / 2, height / 2, depth / 2)
                  .setTranslation(x, height / 2, z)
                  .setCollisionGroups(interactionGroups(CollisionLayer.WORLD, 0xffff)),
              );
            }
            collider.setEnabled(false);
            colliders.push(collider);
          }
        }
        await checkpoint();
      }
      const buildingTile = await buildingModels.createTileIncremental(
        buildingInputs,
        checkpoint,
        signal,
      );
      buildingTile.root.visible = !["vehicle-crash", "footprint-gap"].includes(this.#spawnId);
      root.add(buildingTile.root);
      const sidewalkMaterial = standardMaterial(0xffffff, 0.91, 0.02, this.#textures.sidewalk);
      const roadMaterial = standardMaterial(0xffffff, 0.77, 0.02, this.#textures.asphalt);
      detachedRoadMaterials = [sidewalkMaterial, roadMaterial];
      const roadBatches = await buildRoadBatches(
        decoded.roads,
        sidewalkMaterial,
        roadMaterial,
        checkpoint,
        signal,
      );
      root.add(roadBatches.root);
      detachedRoadMaterials = [];
      vegetation = await this.vegetation.prepareTile(
        {
          id: entry.id,
          tileX: entry.x,
          tileZ: entry.z,
          tileSizeM: this.#mapManifest?.mapTileSizeM ?? MAP_TILE_SIZE_M,
          buildings: decoded.buildings,
          roads: decoded.roads,
        },
        generation,
        checkpoint,
        signal,
      );
      root.add(vegetation.root);
      lighting = await lightingPromise;
      if (lighting) root.add(lighting.root);
      signal.throwIfAborted();
      root.traverse((object) => {
        object.updateMatrix();
        object.matrixAutoUpdate = false;
      });
      return {
        id: entry.id,
        generation,
        root,
        colliders,
        roads: roadRecordsFromPacked(
          decoded.roads,
          decoded.roadAttributes,
          decoded.roadIds,
          entry.id,
        ),
        buildingRender: buildingTile.stats,
        lighting,
        vegetation,
      };
    } catch (error) {
      const settledLighting = lighting ?? (await lightingPromise.catch(() => null));
      if (settledLighting) this.lighting.disposePreparedTile(settledLighting);
      if (vegetation) this.vegetation.disposePreparedTile(vegetation);
      for (const collider of colliders) this.physics.removeCollider(collider, false);
      for (const material of detachedRoadMaterials) material.dispose();
      await disposeObjectIncremental(
        root,
        buildingModels.materials,
        this.#createMapWorkCheckpoint(this.#retirementAbortController.signal, "dispose"),
      );
      throw error;
    }
  }

  async #disposePreparedMapTile(prepared: PreparedMapTile): Promise<void> {
    if (prepared.lighting) this.lighting.disposePreparedTile(prepared.lighting);
    this.vegetation.disposePreparedTile(prepared.vegetation);
    const checkpoint = this.#createMapWorkCheckpoint(
      this.#retirementAbortController.signal,
      "dispose",
    );
    for (const collider of prepared.colliders) {
      this.physics.removeCollider(collider, false);
      await checkpoint();
    }
    prepared.root.removeFromParent();
    await disposeObjectIncremental(prepared.root, this.#buildingModels?.materials, checkpoint);
    this.#recordStreaming();
  }

  #swapPreparedMapTiles(
    desiredEntries: readonly MapTileIndexEntry[],
    staging: MapTileGeneration,
    playerX: number,
    playerZ: number,
  ): void {
    const commitStarted = import.meta.env.PROD ? 0 : performance.now();
    const desiredIds = new Set(desiredEntries.map(({ id }) => id));
    for (const [id, tile] of this.#loadedMapTiles) {
      if (desiredIds.has(id)) continue;
      tile.root.removeFromParent();
      for (const collider of tile.colliders) collider.setEnabled(false);
      const retired: RetiredMapTile = {
        root: tile.root,
        colliders: tile.colliders,
        lighting: this.lighting.retireTile(id),
        vegetation: this.vegetation.retireTile(id),
      };
      this.#loadedMapTiles.delete(id);
      this.#retiredMapTiles.push(retired);
    }
    for (const entry of desiredEntries) {
      const prepared = staging.prepared.get(entry.id);
      if (!prepared) continue;
      for (const collider of prepared.colliders) collider.setEnabled(true);
      if (prepared.lighting) this.lighting.commitPreparedTile(prepared.lighting);
      this.vegetation.commitPreparedTile(prepared.vegetation);
      this.scene.add(prepared.root);
      this.#loadedMapTiles.set(entry.id, {
        root: prepared.root,
        colliders: prepared.colliders,
        roads: prepared.roads,
        buildingRender: prepared.buildingRender,
      });
    }
    staging.prepared.clear();
    this.#roadNetwork = new RoadNetwork(
      [...this.#loadedMapTiles.values()].flatMap((tile) => tile.roads),
    );
    this.#refreshNavigationRoads(playerX, playerZ);
    this.#recordStreaming({
      commitMs: import.meta.env.PROD ? 0 : performance.now() - commitStarted,
      committed: true,
    });
    this.#drainRetiredMapTiles();
  }

  #drainRetiredMapTiles(): void {
    if (this.#retirementDrain || this.#retiredMapTiles.length === 0) return;
    this.#retirementDrain = (async () => {
      while (this.#retiredMapTiles.length > 0) {
        this.#retirementAbortController.signal.throwIfAborted();
        const retired = this.#retiredMapTiles.shift()!;
        for (let frameIndex = 0; frameIndex < 2; frameIndex += 1) {
          const { promise, resolve } = Promise.withResolvers<void>();
          requestAnimationFrame(() => resolve());
          await promise;
          this.#retirementAbortController.signal.throwIfAborted();
        }
        const checkpoint = this.#createMapWorkCheckpoint(
          this.#retirementAbortController.signal,
          "dispose",
        );
        for (const collider of retired.colliders) {
          this.physics.removeCollider(collider, false);
          await checkpoint();
        }
        if (retired.lighting) this.lighting.disposeRetiredTile(retired.lighting);
        if (retired.vegetation)
          await this.vegetation.disposeRetiredTileIncremental(retired.vegetation, checkpoint);
        await disposeObjectIncremental(retired.root, this.#buildingModels?.materials, checkpoint);
        this.#recordStreaming();
      }
    })()
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError"))
          this.errors.push(error instanceof Error ? error.message : "Map tile retirement failed");
      })
      .finally(() => {
        this.#retirementDrain = undefined;
      });
  }

  #buildInteriors(): void {
    const buildingModels = this.#buildingModels;
    if (!buildingModels) throw new Error("Building models are not loaded");
    const ordinaryFootprint = new Float32Array([33, 18, 51, 18, 51, 38, 33, 38]);
    const ordinary = buildingModels.createTile([
      {
        x: 42,
        z: 28,
        width: 18,
        depth: 20,
        height: 24,
        footprint: ordinaryFootprint,
        footprintStart: 0,
        footprintEnd: 4,
      },
    ]);
    this.#ordinaryBuildingStats = ordinary.stats;
    this.scene.add(ordinary.root);
    this.physics.createCollider(
      RAPIER.ColliderDesc.cuboid(9, 12, 10)
        .setTranslation(42, 12, 28)
        .setCollisionGroups(interactionGroups(CollisionLayer.WORLD, 0xffff)),
    );
    const entrance = new THREE.Mesh(
      roundedBox(3.2, 4.2, 0.4, 0.35),
      standardMaterial(0x1c3438, 0.42),
    );
    entrance.position.set(42, 2.1, 38.2);
    this.scene.add(entrance);

    const roomMaterial = standardMaterial(0x777468, 0.9);
    const floorMaterial = standardMaterial(0x4c524d, 0.96);
    for (const [centerX, centerZ, width, depth] of [
      [238, 238, 18, 20],
      [-238, 238, 42, 28],
    ] as const) {
      const floor = new THREE.Mesh(roundedBox(width, 0.25, depth, 0.12), floorMaterial);
      floor.position.set(centerX, 0.1, centerZ);
      this.scene.add(floor);
      const walls = [
        { x: centerX, z: centerZ - depth / 2, width, depth: 0.35 },
        { x: centerX - width / 2, z: centerZ, width: 0.35, depth },
        { x: centerX + width / 2, z: centerZ, width: 0.35, depth },
        { x: centerX - width / 3, z: centerZ + depth / 2, width: width / 3, depth: 0.35 },
        { x: centerX + width / 3, z: centerZ + depth / 2, width: width / 3, depth: 0.35 },
      ];
      for (const wall of walls) {
        const mesh = new THREE.Mesh(roundedBox(wall.width, 4.5, wall.depth, 0.12), roomMaterial);
        mesh.position.set(wall.x, 2.25, wall.z);
        this.scene.add(mesh);
        this.physics.createCollider(
          RAPIER.ColliderDesc.cuboid(wall.width / 2, 2.25, wall.depth / 2)
            .setTranslation(wall.x, 2.25, wall.z)
            .setCollisionGroups(interactionGroups(CollisionLayer.WORLD, 0xffff)),
        );
      }
    }
    const stairMaterial = standardMaterial(PALETTE.stone, 0.88);
    for (let step = 0; step < 9; step += 1) {
      const y = 0.18 + step * 0.35;
      const z = 242 - step * 0.78;
      const stair = new THREE.Mesh(roundedBox(3.4, 0.35, 0.9, 0.1), stairMaterial);
      stair.position.set(241, y, z);
      this.scene.add(stair);
      this.physics.createCollider(
        RAPIER.ColliderDesc.cuboid(1.7, 0.175, 0.45)
          .setTranslation(241, y, z)
          .setCollisionGroups(interactionGroups(CollisionLayer.WORLD, 0xffff)),
      );
    }
    const upperFloor = new THREE.Mesh(roundedBox(18, 0.28, 7.2, 0.12), floorMaterial);
    upperFloor.position.set(238, 3.25, 231.6);
    this.scene.add(upperFloor);
    this.physics.createCollider(
      RAPIER.ColliderDesc.cuboid(9, 0.14, 3.6)
        .setTranslation(238, 3.25, 231.6)
        .setCollisionGroups(interactionGroups(CollisionLayer.WORLD, 0xffff)),
    );
    const lift = new THREE.Mesh(roundedBox(3.1, 4.2, 3.1, 0.24), standardMaterial(0x33454a, 0.55));
    lift.position.set(233, 2.1, 231);
    this.scene.add(lift);
    for (let track = -1; track <= 1; track += 2) {
      const rail = new THREE.Mesh(roundedBox(36, 0.12, 4.2, 0.1), standardMaterial(0x252d2f, 0.78));
      rail.position.set(-238, 0.35, 238 + track * 7);
      this.scene.add(rail);
    }
    const train = new THREE.Group();
    const trainBody = new THREE.Mesh(
      roundedBox(25.9, 4, 3.2, 0.32),
      standardMaterial(0x49626b, 0.52),
    );
    trainBody.position.y = 2.2;
    train.add(trainBody);
    const trainRoute = this.#textSprite("HARLEM LINE", "#f4d978", 320, 52);
    trainRoute.position.set(0, 2.8, 1.65);
    trainRoute.scale.set(7, 0.9, 1);
    train.add(trainRoute);
    train.position.set(-238, 0.1, 238);
    train.userData.speed = 6.5;
    train.userData.transit = true;
    train.userData.path = [-260, 238, 260, 238];
    train.userData.progress = 22 / 520;
    this.scene.add(train);
    const trainRigidBody = this.physics.createRigidBody(
      RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(
        train.position.x,
        train.position.y,
        train.position.z,
      ),
    );
    this.physics.createCollider(
      RAPIER.ColliderDesc.cuboid(12.95, 2, 1.6)
        .setTranslation(0, 2.1, 0)
        .setCollisionGroups(interactionGroups(CollisionLayer.TRANSIT, CollisionLayer.PLAYER)),
      trainRigidBody,
    );
    this.#dynamicBodies.set(train, trainRigidBody);
    this.#train = train;
    const stationSign = this.#textSprite("GRAND CENTRAL · TRACKS 23–24", "#f4d978", 640, 56);
    stationSign.position.set(-238, 3.1, 224.2);
    stationSign.scale.set(24, 2.1, 1);
    this.scene.add(stationSign);
  }

  #fixtureTrafficRoutes(roads: readonly RoadRecord[]): LaneEdge[] {
    const routes = roads
      .flatMap(laneEdgesForRoad)
      .filter(({ fromX, fromZ, toX, toZ }) => Math.hypot(toX - fromX, toZ - fromZ) >= 24)
      .sort(
        (a, b) =>
          Math.hypot(
            (a.fromX + a.toX) / 2 - this.#mapCenter.x,
            (a.fromZ + a.toZ) / 2 - this.#mapCenter.y,
          ) -
          Math.hypot(
            (b.fromX + b.toX) / 2 - this.#mapCenter.x,
            (b.fromZ + b.toZ) / 2 - this.#mapCenter.y,
          ),
      )
      .slice(0, POPULATION_ROUTE_LIMIT);
    if (routes.length === 0) throw new Error("No mapped travel lanes are available for traffic");
    return routes;
  }

  #closedSegmentRoute(
    id: string,
    fromX: number,
    fromZ: number,
    toX: number,
    toZ: number,
    speedMps: number,
    laneId = id,
  ): VehicleRoute {
    return createVehicleRoute(
      id,
      [laneId],
      ["fixture"],
      [fromX, fromZ, toX, toZ, fromX, fromZ],
      speedMps,
    );
  }

  #buildStaticTraffic(roads: readonly RoadRecord[]): void {
    const routes = this.#fixtureTrafficRoutes(roads);
    if (this.#spawnId === "vehicle-crash") {
      const z = this.#mapCenter.y - 4;
      this.vehicles.spawnCar({
        route: this.#closedSegmentRoute(
          "fixture:crash:0",
          this.#mapCenter.x,
          z,
          this.#mapCenter.x + 100,
          z,
          0,
        ),
        progress: 0,
        speedMps: 0,
        type: "sedan",
        color: CIVILIAN_VEHICLE_COLORS[0],
        health: 30,
      });
      this.vehicles.spawnCar({
        route: this.#closedSegmentRoute(
          "fixture:crash:1",
          this.#mapCenter.x + 14,
          z,
          this.#mapCenter.x + 114,
          z,
          0,
        ),
        progress: 0,
        speedMps: 0,
        type: "sedan",
        color: CIVILIAN_VEHICLE_COLORS[2],
        health: 30,
      });
      return;
    }
    if (this.#spawnId === "vehicle-showcase") {
      const showcaseTypes = [
        "compact",
        "hatchback",
        "sedan",
        "coupe",
        "wagon",
        "crossover",
        "suv",
        "minivan",
        "pickup",
        "cargo-van",
        "taxi",
        "police",
      ] as const satisfies readonly VehicleType[];
      const frontOffsets = [-10.5, -6.3, -2.1, 2.1, 6.3, 10.5] as const;
      const rearOffsets = [-8.4, -4.2, 0, 4.2, 8.4, 12.6] as const;
      for (let index = 0; index < showcaseTypes.length; index += 1) {
        const type = showcaseTypes[index]!;
        const frontRow = index < frontOffsets.length;
        const x =
          this.#mapCenter.x +
          (frontRow ? frontOffsets[index]! : rearOffsets[index - frontOffsets.length]!);
        const z = this.#mapCenter.y + (frontRow ? -8 : -15);
        const route = this.#closedSegmentRoute(
          `fixture:showcase:${index}`,
          x,
          z,
          x,
          z + (frontRow ? 100 : -100),
          0,
        );
        if (type === "taxi" || type === "police") {
          this.vehicles.spawnCar({ route, progress: 0, speedMps: 0, type });
        } else {
          this.vehicles.spawnCar({
            route,
            progress: 0,
            speedMps: 0,
            type,
            color: CIVILIAN_VEHICLE_COLORS[index % CIVILIAN_VEHICLE_COLORS.length]!,
          });
        }
      }
      return;
    }
    const carCount = this.#spawnId === "bus-stop" ? 0 : 24;
    for (let index = 0; index < carCount; index += 1) {
      const lane = routes[index % routes.length]!;
      const speedMps = Math.min(lane.speedMps, index % 3 === 0 ? 7 : 4);
      this.vehicles.spawnCar({
        route: this.#closedSegmentRoute(
          `fixture:${lane.id}:${index}`,
          lane.fromX,
          lane.fromZ,
          lane.toX,
          lane.toZ,
          speedMps,
          lane.id,
        ),
        progress: (((index * 0.381_966) % 1) + 1) % 1,
        speedMps,
        ...trafficAppearanceAt(index),
      });
    }
    this.#buildTransitAndTrafficLights(roads);
  }

  #buildTransitAndTrafficLights(roads: readonly RoadRecord[]): void {
    const lanes = roads.flatMap(laneEdgesForRoad);
    const busLane =
      this.#spawnId === "bus-stop"
        ? undefined
        : lanes.reduce<LaneEdge | undefined>((longest, lane) => {
            if (!longest) return lane;
            const length = Math.hypot(lane.toX - lane.fromX, lane.toZ - lane.fromZ);
            const longestLength = Math.hypot(
              longest.toX - longest.fromX,
              longest.toZ - longest.fromZ,
            );
            const rightRank = lane.direction === "FT" ? -lane.laneIndex : lane.laneIndex;
            const longestRightRank =
              longest.direction === "FT" ? -longest.laneIndex : longest.laneIndex;
            return length > longestLength ||
              (length === longestLength && rightRank > longestRightRank)
              ? lane
              : longest;
          }, undefined);
    if (!busLane && this.#spawnId !== "bus-stop")
      throw new Error("No mapped travel lane is available for transit");
    const connectedBusRoute = busLane
      ? new RoadNetwork(roads)
          .routesNear({ x: this.#mapCenter.x, z: this.#mapCenter.y }, 520, POPULATION_ROUTE_LIMIT)
          .find((route) => route.laneIds.includes(busLane.id))
      : undefined;
    const busProgress = this.#spawnId === "bus-stop" ? 0.3 : 0.15;
    let segmentProgress = busProgress;
    let busFromX = busLane?.fromX ?? -80;
    let busFromZ = busLane?.fromZ ?? 4;
    let busToX = busLane?.toX ?? 80;
    let busToZ = busLane?.toZ ?? 4;
    if (connectedBusRoute) {
      const distanceM = connectedBusRoute.lengthM * busProgress;
      let segmentIndex = 1;
      while (
        segmentIndex < connectedBusRoute.cumulativeLengths.length - 1 &&
        (connectedBusRoute.cumulativeLengths[segmentIndex] ?? 0) < distanceM
      )
        segmentIndex += 1;
      const offset = segmentIndex * 2;
      const startDistance = connectedBusRoute.cumulativeLengths[segmentIndex - 1] ?? 0;
      const segmentLength =
        (connectedBusRoute.cumulativeLengths[segmentIndex] ?? connectedBusRoute.lengthM) -
        startDistance;
      segmentProgress = segmentLength > 0 ? (distanceM - startDistance) / segmentLength : 0;
      busFromX = connectedBusRoute.points[offset - 2] ?? busFromX;
      busFromZ = connectedBusRoute.points[offset - 1] ?? busFromZ;
      busToX = connectedBusRoute.points[offset] ?? busToX;
      busToZ = connectedBusRoute.points[offset + 1] ?? busToZ;
    }
    const bus = new THREE.Group();
    const busBody = new THREE.Mesh(
      roundedBox(12, 3.2, 2.55, 0.32),
      standardMaterial(0x2b6874, 0.58),
    );
    busBody.position.y = 1.8;
    bus.add(busBody);
    const route = this.#textSprite("M42  EAST SIDE", "#f4d978", 320, 52);
    route.position.set(5.95, 2.3, 0);
    route.rotation.y = Math.PI / 2;
    route.scale.set(4.8, 0.75, 1);
    bus.add(route);
    bus.position.set(
      THREE.MathUtils.lerp(busFromX, busToX, segmentProgress),
      0.1,
      THREE.MathUtils.lerp(busFromZ, busToZ, segmentProgress),
    );
    bus.rotation.y = -Math.atan2(busToZ - busFromZ, busToX - busFromX);
    bus.userData.speed = this.#spawnId === "bus-stop" ? 0 : Math.min(5.5, busLane?.speedMps ?? 5.5);
    bus.userData.path = [busFromX, busFromZ, busToX, busToZ];
    bus.userData.progress = busProgress;
    if (connectedBusRoute) {
      bus.userData.vehicleRoute = connectedBusRoute;
      bus.userData.routeDistanceM = connectedBusRoute.lengthM * busProgress;
    }
    bus.userData.transit = true;
    this.scene.add(bus);
    const busRigidBody = this.physics.createRigidBody(
      RAPIER.RigidBodyDesc.kinematicPositionBased()
        .setTranslation(bus.position.x, bus.position.y, bus.position.z)
        .setRotation({
          x: 0,
          y: Math.sin(bus.rotation.y / 2),
          z: 0,
          w: Math.cos(bus.rotation.y / 2),
        }),
    );
    this.physics.createCollider(
      RAPIER.ColliderDesc.cuboid(6, 1.6, 1.275)
        .setTranslation(0, 1.8, 0)
        .setCollisionGroups(interactionGroups(CollisionLayer.TRANSIT, CollisionLayer.PLAYER)),
      busRigidBody,
    );
    this.#dynamicBodies.set(bus, busRigidBody);
    this.#bus = bus;
  }

  #buildPedestrians(roads: readonly RoadRecord[]): void {
    const routes = roads
      .map((road) => road.segment)
      .filter(([fromX, fromZ, toX, toZ]) => Math.hypot(toX - fromX, toZ - fromZ) >= 16)
      .sort(
        (a, b) =>
          Math.hypot((a[0] + a[2]) / 2 - this.#mapCenter.x, (a[1] + a[3]) / 2 - this.#mapCenter.y) -
          Math.hypot((b[0] + b[2]) / 2 - this.#mapCenter.x, (b[1] + b[3]) / 2 - this.#mapCenter.y),
      )
      .slice(0, POPULATION_ROUTE_LIMIT);
    if (routes.length === 0)
      throw new Error("No mapped sidewalk segments are available for pedestrians");
    for (let index = 0; index < 72; index += 1) {
      this.#spawnPedestrian(
        routes[(index * 11) % routes.length]!,
        index,
        "fixture",
        this.#spawnId === "pedestrian-collision" && index === 0,
      );
    }
  }

  #spawnPedestrian(
    route: RoadSegment,
    sequence: number,
    tileId: string,
    collisionFixture = false,
  ): void {
    const [fromX, fromZ, toX, toZ, width] = route;
    const length = Math.hypot(toX - fromX, toZ - fromZ);
    const normalX = -(toZ - fromZ) / length;
    const normalZ = (toX - fromX) / length;
    const sidewalkOffset = (sequence % 2 ? 1 : -1) * (width / 2 + 1.2);
    const pathFromX = collisionFixture ? 0 : fromX + normalX * sidewalkOffset;
    const pathFromZ = collisionFixture ? 4 : fromZ + normalZ * sidewalkOffset;
    const pathToX = collisionFixture ? 0 : toX + normalX * sidewalkOffset;
    const pathToZ = collisionFixture ? 4 : toZ + normalZ * sidewalkOffset;
    const progress = collisionFixture ? 0 : (((sequence * 0.618_034) % 1) + 1) % 1;
    const colors = [0x8f4f40, 0x3b7180, 0x796a3d, 0x583f6f, 0x4d6354];
    const person = new THREE.Group();
    person.userData.pedestrian = true;
    person.userData.speed = collisionFixture ? 0 : 1.2 + (sequence % 7) * 0.1;
    person.userData.path = [pathFromX, pathFromZ, pathToX, pathToZ];
    person.userData.progress = progress;
    const body = new THREE.Mesh(
      roundedBox(0.5, 0.9, 0.34, 0.15),
      standardMaterial(colors[sequence % colors.length] ?? 0xffffff, 0.85),
    );
    body.position.y = 1;
    const head = new THREE.Mesh(
      roundedBox(0.33, 0.33, 0.33, 0.13),
      standardMaterial(sequence % 3 === 0 ? 0x6c4535 : 0xb57b5f, 0.95),
    );
    head.position.y = 1.65;
    person.add(body, head);
    person.position.set(
      THREE.MathUtils.lerp(pathFromX, pathToX, progress),
      0.2,
      THREE.MathUtils.lerp(pathFromZ, pathToZ, progress),
    );
    person.rotation.y = -Math.atan2(pathToZ - pathFromZ, pathToX - pathFromX);
    this.scene.add(person);
    const rigidBody = this.physics.createRigidBody(
      RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(
        person.position.x,
        person.position.y,
        person.position.z,
      ),
    );
    this.physics.createCollider(
      RAPIER.ColliderDesc.capsule(0.55, 0.25)
        .setTranslation(0, 0.9, 0)
        .setCollisionGroups(interactionGroups(CollisionLayer.NPC, CollisionLayer.PLAYER)),
      rigidBody,
    );
    this.#dynamicBodies.set(person, rigidBody);
    this.#pedestrians.push({ group: person, rigidBody, tileId, sequence });
  }

  #removePedestrian(record: PedestrianRecord): void {
    this.scene.remove(record.group);
    this.#dynamicBodies.delete(record.group);
    this.physics.removeRigidBody(record.rigidBody);
    disposeObject(record.group);
    const index = this.#pedestrians.indexOf(record);
    if (index >= 0) this.#pedestrians.splice(index, 1);
  }

  #reconcilePedestrians(
    center: Readonly<{ x: number; z: number }>,
    retainRadiusM: number,
    targetCount: number,
    activeTileIds: ReadonlySet<string>,
    routes: readonly LoadedPopulationRoute[],
  ): void {
    for (let index = this.#pedestrians.length - 1; index >= 0; index -= 1) {
      const record = this.#pedestrians[index]!;
      const distance = Math.hypot(
        record.group.position.x - center.x,
        record.group.position.z - center.z,
      );
      if (!activeTileIds.has(record.tileId) || distance > retainRadiusM)
        this.#removePedestrian(record);
    }
    if (this.#pedestrians.length > targetCount) {
      const excess = [...this.#pedestrians]
        .sort(
          (left, right) =>
            Math.hypot(right.group.position.x - center.x, right.group.position.z - center.z) -
            Math.hypot(left.group.position.x - center.x, left.group.position.z - center.z),
        )
        .slice(0, this.#pedestrians.length - targetCount);
      for (const record of excess) this.#removePedestrian(record);
    }
    while (this.#pedestrians.length < targetCount && routes.length > 0) {
      const sequence = this.#nextPedestrianSequence++;
      const route = routes[(sequence * 11) % routes.length]!;
      this.#spawnPedestrian(route.segment, sequence, route.tileId);
    }
  }

  #reconcileAmbientPopulation(center: Readonly<{ x: number; z: number }>, force = false): void {
    if (this.#usesStaticPopulation) return;
    const cell = `${Math.floor(center.x / POPULATION_CELL_SIZE_M)}:${Math.floor(center.z / POPULATION_CELL_SIZE_M)}`;
    if (!force && cell === this.#lastPopulationCell) return;
    this.#lastPopulationCell = cell;
    const activeTileIds = new Set(this.#loadedMapTiles.keys());
    const loadedRoutes: LoadedPopulationRoute[] = [];
    for (const [tileId, tile] of this.#loadedMapTiles) {
      for (const road of tile.roads) loadedRoutes.push({ tileId, segment: road.segment });
    }
    const selectRoutes = (minimumLength: number): LoadedPopulationRoute[] =>
      loadedRoutes
        .filter(({ segment: [fromX, fromZ, toX, toZ] }) => {
          if (Math.hypot(toX - fromX, toZ - fromZ) < minimumLength) return false;
          return (
            Math.hypot((fromX + toX) / 2 - center.x, (fromZ + toZ) / 2 - center.z) <=
            POPULATION_SPAWN_RADIUS_M
          );
        })
        .sort(
          (left, right) =>
            Math.hypot(
              (left.segment[0] + left.segment[2]) / 2 - center.x,
              (left.segment[1] + left.segment[3]) / 2 - center.z,
            ) -
            Math.hypot(
              (right.segment[0] + right.segment[2]) / 2 - center.x,
              (right.segment[1] + right.segment[3]) / 2 - center.z,
            ),
        )
        .slice(0, POPULATION_ROUTE_LIMIT);
    const carRoutes = this.#roadNetwork.routesNear(
      center,
      POPULATION_SPAWN_RADIUS_M,
      POPULATION_ROUTE_LIMIT,
    );
    const pedestrianRoutes = selectRoutes(16);
    this.vehicles.reconcileAmbientTraffic(
      center,
      POPULATION_RETAIN_RADIUS_M,
      this.streamer.profile.vehicles,
      activeTileIds,
      (sequence) => {
        if (carRoutes.length === 0) return undefined;
        const route = carRoutes[sequence % carRoutes.length]!;
        const spec: CarSpawn = {
          route,
          progress: (((sequence * 0.381_966) % 1) + 1) % 1,
          speedMps: Math.min(route.speedMps, 8),
          ...trafficAppearanceAt(sequence),
        };
        return { tileIds: route.tileIds, spec };
      },
    );
    this.#reconcilePedestrians(
      center,
      POPULATION_RETAIN_RADIUS_M,
      this.streamer.profile.pedestrians,
      activeTileIds,
      pedestrianRoutes,
    );
  }

  #buildWeather(): void {
    const positions = new Float32Array(1800 * 3);
    const random = this.#random(42);
    for (let index = 0; index < positions.length; index += 3) {
      positions[index] = (random() - 0.5) * 120;
      positions[index + 1] = random() * 70;
      positions[index + 2] = (random() - 0.5) * 120;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    this.#rain = new THREE.Points(
      geometry,
      new THREE.PointsMaterial({ color: 0x9bc9d0, size: 0.12, transparent: true, opacity: 0.48 }),
    );
    this.scene.add(this.#rain);
  }

  #textSprite(text: string, color: string, width: number, height: number): THREE.Sprite {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Canvas 2D unavailable");
    context.fillStyle = "rgba(7,12,13,.9)";
    context.fillRect(0, 0, width, height);
    context.fillStyle = color;
    context.font = "700 25px system-ui";
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(text, width / 2, height / 2);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    return new THREE.Sprite(
      new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: true }),
    );
  }
  #random(seed: number): () => number {
    let value = seed;
    return () => {
      value = Math.imul(value ^ (value >>> 15), value | 1);
      value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
      return ((value ^ (value >>> 14)) >>> 0) / 0x1_0000_0000;
    };
  }

  #tick = (now: number): void => {
    if (this.#disposed) return;
    this.#raf = requestAnimationFrame(this.#tick);
    const rawFrameMs = now - this.#lastFrame;
    const rawFrameSeconds = Math.max(0, rawFrameMs / 1000);
    const frame = Math.min(MAX_FRAME_DELTA_SECONDS, rawFrameSeconds);
    this.#lastFrame = now;
    const monitor = this.#performanceMonitor;
    const sample = this.#framePerformanceSample;
    const cpuStarted = monitor ? performance.now() : 0;
    sample.rawFrameMs = rawFrameMs;
    sample.fixedPhysicsMs = 0;
    sample.simulationMs = 0;
    sample.animateCityMs = 0;
    sample.wallUpdateMs = 0;
    sample.vehicleFrameMs = 0;
    sample.systemFrameMs = 0;
    sample.lightingFrameMs = 0;
    sample.skyFrameMs = 0;
    sample.renderMs = 0;
    sample.fixedSteps = 0;
    sample.simulationSteps = 0;
    sample.droppedDebtMs = (rawFrameSeconds - frame) * 1_000;
    if (!this.#paused) {
      this.#physicsDebt += frame;
      this.#simulationDebt += frame;
      const physicsAdvance = advanceFixedDebt(
        this.#physicsDebt,
        FIXED_STEP,
        MAX_PHYSICS_STEPS_PER_FRAME,
      );
      this.#physicsDebt = physicsAdvance.remainderSeconds;
      sample.fixedSteps = physicsAdvance.steps;
      sample.droppedDebtMs += physicsAdvance.droppedSeconds * 1_000;
      let phaseStarted = monitor ? performance.now() : 0;
      for (let step = 0; step < physicsAdvance.steps; step += 1) {
        this.player.fixedUpdate(FIXED_STEP);
        this.vehicles.fixedUpdate(FIXED_STEP);
        for (const system of this.systems) system.fixedUpdate(FIXED_STEP);
        this.physics.step(this.vehicles.eventQueue);
        this.vehicles.afterPhysics();
      }
      if (monitor) sample.fixedPhysicsMs = performance.now() - phaseStarted;
      const simulationAdvance = advanceFixedDebt(
        this.#simulationDebt,
        SIM_STEP,
        MAX_SIMULATION_STEPS_PER_FRAME,
      );
      this.#simulationDebt = simulationAdvance.remainderSeconds;
      sample.simulationSteps = simulationAdvance.steps;
      sample.droppedDebtMs += simulationAdvance.droppedSeconds * 1_000;
      phaseStarted = monitor ? performance.now() : 0;
      for (let step = 0; step < simulationAdvance.steps; step += 1)
        this.simulation.fixedUpdate(SIM_STEP);
      if (monitor) sample.simulationMs = performance.now() - phaseStarted;
      phaseStarted = monitor ? performance.now() : 0;
      this.#animateCity(frame);
      if (monitor) sample.animateCityMs = performance.now() - phaseStarted;
    }
    const wallSecond = Math.floor(Number(this.clock.instant().epochMilliseconds) / 1000);
    if (wallSecond !== this.#lastWallSecond) {
      this.#lastWallSecond = wallSecond;
      const phaseStarted = monitor ? performance.now() : 0;
      this.#wallUpdate();
      if (monitor) sample.wallUpdateMs = performance.now() - phaseStarted;
    }
    const alpha = this.#physicsDebt / FIXED_STEP;
    let phaseStarted = monitor ? performance.now() : 0;
    this.vehicles.frameUpdate(alpha);
    const mapPlayerPosition = this.player.body.translation();
    const mapDesired = this.#desiredMapEntries(mapPlayerPosition.x, mapPlayerPosition.z);
    if (mapDesired && mapDesired.key !== this.#desiredMapTileKey)
      this.#queueMapReconcile(mapPlayerPosition.x, mapPlayerPosition.z);
    if (monitor) sample.vehicleFrameMs = performance.now() - phaseStarted;
    phaseStarted = monitor ? performance.now() : 0;
    for (const system of this.systems) system.frameUpdate(alpha);
    if (monitor) sample.systemFrameMs = performance.now() - phaseStarted;
    phaseStarted = monitor ? performance.now() : 0;
    this.lighting.frameUpdate(this.player.root.position);
    if (monitor) sample.lightingFrameMs = performance.now() - phaseStarted;
    phaseStarted = monitor ? performance.now() : 0;
    this.#sky.frameUpdate(this.#paused || this.#reducedMotion ? 0 : frame);
    if (monitor) sample.skyFrameMs = performance.now() - phaseStarted;
    phaseStarted = monitor ? performance.now() : 0;
    this.renderer.render(this.scene, this.camera);
    if (monitor) {
      sample.renderMs = performance.now() - phaseStarted;
      sample.cpuFrameMs = performance.now() - cpuStarted;
      sample.rendererCalls = this.renderer.info.render.calls;
      sample.triangles = this.renderer.info.render.triangles;
      monitor.recordFrame(sample);
    }
  };

  #animateCity(frame: number): void {
    for (const [object, rigidBody] of this.#dynamicBodies) {
      const speed = object.userData.speed as number | undefined;
      if (!speed) continue;
      const vehicleRoute = object.userData.vehicleRoute as VehicleRoute | undefined;
      let fromX: number;
      let fromZ: number;
      let toX: number;
      let toZ: number;
      let progress: number;
      if (vehicleRoute) {
        const distanceM =
          (((object.userData.routeDistanceM as number | undefined) ?? 0) + speed * frame) %
          vehicleRoute.lengthM;
        object.userData.routeDistanceM = distanceM;
        let segmentIndex = 1;
        while (
          segmentIndex < vehicleRoute.cumulativeLengths.length - 1 &&
          (vehicleRoute.cumulativeLengths[segmentIndex] ?? 0) < distanceM
        )
          segmentIndex += 1;
        const offset = segmentIndex * 2;
        fromX = vehicleRoute.points[offset - 2] ?? 0;
        fromZ = vehicleRoute.points[offset - 1] ?? 0;
        toX = vehicleRoute.points[offset] ?? fromX;
        toZ = vehicleRoute.points[offset + 1] ?? fromZ;
        const startDistance = vehicleRoute.cumulativeLengths[segmentIndex - 1] ?? 0;
        const segmentLength =
          (vehicleRoute.cumulativeLengths[segmentIndex] ?? vehicleRoute.lengthM) - startDistance;
        progress = segmentLength > 0 ? (distanceM - startDistance) / segmentLength : 0;
      } else {
        const path = object.userData.path as [number, number, number, number] | undefined;
        if (!path) continue;
        [fromX, fromZ, toX, toZ] = path;
        const length = Math.hypot(toX - fromX, toZ - fromZ);
        progress =
          (((object.userData.progress as number | undefined) ?? 0) + (speed * frame) / length) % 1;
        object.userData.progress = progress;
      }
      object.position.x = THREE.MathUtils.lerp(fromX, toX, progress);
      object.position.z = THREE.MathUtils.lerp(fromZ, toZ, progress);
      const rotation = -Math.atan2(toZ - fromZ, toX - fromX);
      object.rotation.y = rotation;
      rigidBody.setNextKinematicTranslation({
        x: object.position.x,
        y: object.position.y,
        z: object.position.z,
      });
      rigidBody.setNextKinematicRotation({
        x: 0,
        y: Math.sin(rotation / 2),
        z: 0,
        w: Math.cos(rotation / 2),
      });
    }
    if (this.#aboard) {
      const vehicle = this.#aboard === "bus" ? this.#bus : this.#train;
      if (vehicle) {
        this.player.body.setTranslation(
          { x: vehicle.position.x, y: 1.4, z: vehicle.position.z },
          true,
        );
        this.camera.position.lerp(
          new THREE.Vector3(vehicle.position.x - 9, 5.2, vehicle.position.z + 7),
          0.15,
        );
        this.camera.lookAt(vehicle.position.x, 1.8, vehicle.position.z);
      }
    }
    if (this.#rain?.visible) {
      this.#rain.position.copy(this.player.root.position);
      const positions = this.#rain.geometry.attributes.position;
      if (positions)
        for (let index = 1; index < positions.count * 3; index += 3) {
          positions.array[index] = ((positions.array[index] as number) - frame * 28 + 70) % 70;
        }
      positions!.needsUpdate = true;
    }
  }

  #wallUpdate(): void {
    const zoned = this.clock.nowZoned();
    this.#weather = this.weatherService.stateAt(zoned);
    if (this.#weatherOverride) {
      const category = this.#weatherOverride;
      this.#weather = {
        category,
        cloud: category === "clear" ? 0.08 : 0.9,
        fog: category === "fog" ? 0.85 : 0.08,
        precipitation: category === "rain" || category === "snow" || category === "storm" ? 1 : 0,
        blend: 1,
      };
    }
    const instant = this.clock.instant();
    const celestial = this.astronomy.stateAt(new Date(Number(instant.epochMilliseconds)));
    const skyState = this.#sky.update(celestial, this.#weather);
    const artificialLight = 1 - skyState.daylight;
    this.#buildingModels?.setArtificialLight(artificialLight);
    this.lighting.update(Number(instant.epochMilliseconds) / 1000, artificialLight);
    this.vehicles.setLightsEnabled(artificialLight > 0);
    if (this.#rain) this.#rain.visible = this.#weather.precipitation > 0;
    const playerPosition = this.player.body.translation();
    this.streamer.reconcile(playerPosition.x, playerPosition.z);
    this.#queueMapReconcile(playerPosition.x, playerPosition.z);
    this.#refreshNavigationRoads(playerPosition.x, playerPosition.z);
  }

  readonly #resize = (): void => {
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setPixelRatio(Math.min(this.streamer.profile.dpr, window.devicePixelRatio));
    this.renderer.setSize(width, height, false);
  };
  readonly #visibility = (): void => {
    if (document.hidden) this.pause();
    else this.resynchronize();
  };

  play(): void {
    this.#paused = false;
    this.player.enabled = true;
    this.#lastFrame = performance.now();
  }
  pause(): void {
    this.#paused = true;
    this.player.enabled = false;
    this.#physicsDebt = 0;
    this.#simulationDebt = 0;
  }
  resynchronize(): void {
    this.#physicsDebt = 0;
    this.#simulationDebt = 0;
    this.#lastFrame = performance.now();
    this.#wallUpdate();
  }
  #applyProfileShadowBudgets(): void {
    const profile = this.streamer.profile;
    this.lighting.setShadowBudget(profile.streetLightShadowCount);
    this.vehicles.setShadowBudget(profile.headlightShadowVehicleCount);
  }

  setProfile(profile: QualityProfile): void {
    this.streamer.setProfile(profile);
    this.#applyProfileShadowBudgets();
    this.#resize();
    const position = this.player.body.translation();
    this.#reconcileAmbientPopulation(position, true);
  }
  resetPerformanceMetrics(): void {
    this.#performanceMonitor?.reset();
  }
  performanceSnapshot(): PerformanceSnapshot {
    if (!this.#performanceMonitor)
      throw new Error("Performance diagnostics are unavailable in production");
    return this.#performanceMonitor.snapshot();
  }
  preparePerformanceDrive(): PerformanceDriveSetup {
    if (import.meta.env.PROD)
      throw new Error("Performance drive setup is unavailable in production");
    const lanes = [...this.#loadedMapTiles.values()]
      .flatMap((tile) => tile.roads)
      .flatMap(laneEdgesForRoad)
      .sort((left, right) => left.id.localeCompare(right.id));
    const occupied = this.vehicles.snapshot().cars;
    for (const lane of lanes) {
      const deltaX = lane.toX - lane.fromX;
      const deltaZ = lane.toZ - lane.fromZ;
      const length = Math.hypot(deltaX, deltaZ);
      if (length === 0) continue;
      for (const axis of ["x", "z"] as const) {
        const from = axis === "x" ? lane.fromX : lane.fromZ;
        const to = axis === "x" ? lane.toX : lane.toZ;
        const delta = to - from;
        if (delta === 0) continue;
        const firstBoundary =
          delta > 0
            ? Math.floor(from / MAP_TILE_SIZE_M + 1) * MAP_TILE_SIZE_M
            : Math.ceil(from / MAP_TILE_SIZE_M - 1) * MAP_TILE_SIZE_M;
        const progress = (firstBoundary - from) / delta;
        const beforeM = progress * length;
        const afterM = (1 - progress) * length;
        if (progress <= 0 || progress >= 1 || beforeM < 5 || afterM < 20) continue;
        const directionX = deltaX / length;
        const directionZ = deltaZ / length;
        const crossingX = lane.fromX + deltaX * progress;
        const crossingZ = lane.fromZ + deltaZ * progress;
        const orthogonalCoordinate = axis === "x" ? crossingZ : crossingX;
        const orthogonalTileOffset =
          ((orthogonalCoordinate % MAP_TILE_SIZE_M) + MAP_TILE_SIZE_M) % MAP_TILE_SIZE_M;
        if (Math.min(orthogonalTileOffset, MAP_TILE_SIZE_M - orthogonalTileOffset) < 40) continue;
        const fromX = crossingX - directionX * 2;
        const fromZ = crossingZ - directionZ * 2;
        if (
          Math.floor(fromX / MAP_TILE_SIZE_M) !== Math.floor(this.#mapCenter.x / MAP_TILE_SIZE_M) ||
          Math.floor(fromZ / MAP_TILE_SIZE_M) !== Math.floor(this.#mapCenter.y / MAP_TILE_SIZE_M)
        )
          continue;
        const beforeEntries = this.#desiredMapEntries(fromX, fromZ);
        const afterEntries = this.#desiredMapEntries(
          crossingX + directionX * 2,
          crossingZ + directionZ * 2,
        );
        if (
          !beforeEntries ||
          !afterEntries ||
          beforeEntries.entries.length !== 9 ||
          afterEntries.entries.length !== 9 ||
          beforeEntries.key === afterEntries.key
        )
          continue;
        if (occupied.some(({ x, z }) => Math.hypot(x - fromX, z - fromZ) < 14)) continue;
        const toX = crossingX + directionX * 20;
        const toZ = crossingZ + directionZ * 20;
        const route = createVehicleRoute(
          `performance:${lane.id}`,
          [lane.id],
          ["performance"],
          [fromX, fromZ, toX, toZ],
          lane.speedMps,
        );
        const carId = this.vehicles.spawnCar({
          route,
          progress: 0,
          speedMps: 0,
          type: "sedan",
          color: 0xb94f43,
          health: 100,
        });
        const playerX = fromX - directionZ * 2;
        const playerZ = fromZ + directionX * 2;
        this.repositionPlayer(playerX, playerZ);
        if (!this.vehicles.enter(carId, false))
          throw new Error("Performance drive vehicle could not be entered");
        return { carId, targetAxis: axis, targetCoordinate: firstBoundary };
      }
    }
    throw new Error("No performance drive lane crosses a map boundary");
  }
  repositionPlayer(x: number, z: number): void {
    if (!Number.isFinite(x) || !Number.isFinite(z))
      throw new RangeError("Player coordinates must be finite");
    if (this.#interior || this.#aboard || this.player.controlMode === "seated")
      throw new Error("Cannot reposition the player from an interior or vehicle");
    this.player.body.setTranslation({ x, y: 2, z }, true);
    this.player.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    this.player.root.position.set(x, 1.1, z);
    this.player.verticalVelocity = 0;
    this.player.grounded = false;
    this.streamer.reconcile(x, z);
    this.#queueMapReconcile(x, z);
  }
  interact():
    | "entered"
    | "exited"
    | "departures"
    | "boarded"
    | "alighted"
    | "car-entered"
    | "car-exited"
    | "exit-blocked"
    | "none" {
    const position = this.player.body.translation();
    if (this.vehicles.snapshot().driving) {
      const result = this.vehicles.exit();
      return result === "exited" ? "car-exited" : result === "blocked" ? "exit-blocked" : "none";
    }
    if (this.#aboard) {
      const vehicle = this.#aboard === "bus" ? this.#bus : this.#train;
      if (vehicle)
        this.player.body.setTranslation(
          { x: vehicle.position.x, y: 1.1, z: vehicle.position.z + 5 },
          true,
        );
      this.#aboard = undefined;
      this.player.root.visible = true;
      return "alighted";
    }
    if (
      this.#bus &&
      Math.hypot(position.x - this.#bus.position.x, position.z - this.#bus.position.z) < 8
    ) {
      this.#aboard = "bus";
      this.#bus.userData.speed = 5.5;
      this.player.root.visible = false;
      return "boarded";
    }
    if (
      this.#train &&
      this.#interior === "grand-central" &&
      Math.hypot(position.x - this.#train.position.x, position.z - this.#train.position.z) < 9
    ) {
      this.#aboard = "train";
      this.#train.userData.speed = 6.5;
      this.player.root.visible = false;
      return "boarded";
    }
    if (!this.#interior) {
      const carId = this.vehicles.nearestHijackable(position, 6);
      if (carId !== null && this.vehicles.enter(carId)) return "car-entered";
    }
    if (
      this.#interior &&
      ((this.#interior === "ordinary" && Math.hypot(position.x - 238, position.z - 246) < 6) ||
        (this.#interior === "grand-central" && Math.hypot(position.x + 238, position.z - 250) < 6))
    ) {
      const previous = this.#interior;
      this.#interior = undefined;
      const exit =
        previous === "ordinary"
          ? { x: 42, y: 1.1, z: 42 }
          : { x: LANDMARKS.grandCentralPlaza.x, y: 1.1, z: LANDMARKS.grandCentralPlaza.y };
      this.player.body.setTranslation(exit, true);
      return "exited";
    }
    if (Math.hypot(position.x - 42, position.z - 42) < 9) {
      this.#interior = "ordinary";
      this.player.body.setTranslation({ x: 238, y: 1.1, z: 246 }, true);
      return "entered";
    }
    if (
      Math.hypot(
        position.x - LANDMARKS.grandCentralEntrance.x,
        position.z - LANDMARKS.grandCentralEntrance.y,
      ) < 18
    ) {
      this.#interior = "grand-central";
      this.player.body.setTranslation({ x: -238, y: 1.1, z: 250 }, true);
      return "entered";
    }
    return Math.hypot(
      position.x - LANDMARKS.grandCentralPlaza.x,
      position.z - LANDMARKS.grandCentralPlaza.y,
    ) < 35
      ? "departures"
      : "none";
  }

  navigationSnapshot(): NavigationSnapshot {
    return this.#buildNavigationSnapshot(this.vehicles.snapshot().cars);
  }

  #buildNavigationSnapshot(cars: CarSnapshot[]): NavigationSnapshot {
    const position = this.player.body.translation();
    const cameraForward = new THREE.Vector3();
    this.camera.getWorldDirection(cameraForward);
    return {
      player: { x: position.x, z: position.z },
      headingDegrees: (Math.atan2(cameraForward.x, -cameraForward.z) * (180 / Math.PI) + 360) % 360,
      roads: this.#navigationRoads,
      cars,
      bus: this.#bus ? { x: this.#bus.position.x, z: this.#bus.position.z } : undefined,
      train: this.#train ? { x: this.#train.position.x, z: this.#train.position.z } : undefined,
    };
  }

  snapshot(): GameSnapshot {
    const position = this.player.body.translation();
    const vehicleSnapshot = this.vehicles.snapshot();
    let exteriorContext = "New York City";
    if (
      Math.hypot(
        position.x - LANDMARKS.centralParkRamble.x,
        position.z - LANDMARKS.centralParkRamble.y,
      ) < 90
    )
      exteriorContext = "Central Park · The Ramble";
    else if (Math.hypot(position.x - 42, position.z - 42) < 24)
      exteriorContext = "East 42nd Street · Building entrance";
    else if (
      Math.hypot(position.x - LANDMARKS.empireState.x, position.z - LANDMARKS.empireState.y) < 80
    )
      exteriorContext = "Empire State · Fifth Avenue";
    else if (
      Math.hypot(position.x - LANDMARKS.timesSquare.x, position.z - LANDMARKS.timesSquare.y) < 100
    )
      exteriorContext = "Times Square · Broadway";
    else if (
      Math.hypot(position.x - LANDMARKS.grandCentral.x, position.z - LANDMARKS.grandCentral.y) < 140
    )
      exteriorContext = "Grand Central · Vanderbilt Plaza";
    else if (Math.hypot(position.x - LANDMARKS.bronxHub.x, position.z - LANDMARKS.bronxHub.y) < 160)
      exteriorContext = "The Bronx · Fordham Plaza";
    else if (
      Math.hypot(position.x - LANDMARKS.brooklynHub.x, position.z - LANDMARKS.brooklynHub.y) < 160
    )
      exteriorContext = "Brooklyn · Borough Hall";
    else if (
      Math.hypot(position.x - LANDMARKS.queensHub.x, position.z - LANDMARKS.queensHub.y) < 160
    )
      exteriorContext = "Queens · Jackson Heights";
    else if (
      Math.hypot(
        position.x - LANDMARKS.statenIslandHub.x,
        position.z - LANDMARKS.statenIslandHub.y,
      ) < 160
    )
      exteriorContext = "Staten Island · St. George";
    const context = this.#aboard
      ? this.#aboard === "bus"
        ? "Aboard M42 · East Side"
        : "Aboard Harlem Line · Grand Central"
      : this.#interior
        ? this.#interior === "ordinary"
          ? "Interior · East 42nd Street"
          : "Grand Central · Track level"
        : exteriorContext;
    const buildingRender = [...this.#loadedMapTiles.values()].reduce<BuildingRenderStats>(
      (total, tile) => ({
        buildings: total.buildings + tile.buildingRender.buildings,
        parts: total.parts + tile.buildingRender.parts,
        meshes: total.meshes + tile.buildingRender.meshes,
        roles: {
          brick: total.roles.brick + tile.buildingRender.roles.brick,
          stone: total.roles.stone + tile.buildingRender.roles.stone,
          glass: total.roles.glass + tile.buildingRender.roles.glass,
          roof: total.roles.roof + tile.buildingRender.roles.roof,
          metal: total.roles.metal + tile.buildingRender.roles.metal,
        },
      }),
      this.#ordinaryBuildingStats,
    );
    const lighting = this.lighting.snapshot();
    const trafficLanes: TrafficLaneSnapshot[] = import.meta.env.PROD
      ? []
      : this.#roadNetwork.lanes
          .filter(
            (lane) =>
              Math.hypot(
                (lane.fromX + lane.toX) / 2 - position.x,
                (lane.fromZ + lane.toZ) / 2 - position.z,
              ) <= POPULATION_SPAWN_RADIUS_M,
          )
          .slice(0, 512)
          .map((lane) => ({
            id: lane.id,
            physicalId: lane.physicalId,
            laneIndex: lane.laneIndex,
            laneCount: lane.laneCount,
            direction: lane.direction,
            fromX: lane.fromX,
            fromZ: lane.fromZ,
            toX: lane.toX,
            toZ: lane.toZ,
          }));
    return {
      player: position,
      chunks: [...this.streamer.residents.keys()],
      mapTiles: [...this.#loadedMapTiles.keys()],
      vegetation: this.vegetation.snapshot(),
      activeCount: this.simulation.activeCount,
      proxyCount: this.simulation.proxyCount,
      abstractCount: this.simulation.abstractCount,
      weather: this.#weather,
      sky: this.#sky.snapshot(),
      context,
      transit: "M42 · East Side",
      vehicles: {
        bus: this.#bus ? { x: this.#bus.position.x, z: this.#bus.position.z } : undefined,
        train: this.#train ? { x: this.#train.position.x, z: this.#train.position.z } : undefined,
      },
      pedestrians: this.#pedestrians.map((record) => ({
        x: record.group.position.x,
        z: record.group.position.z,
      })),
      errors: [...this.errors],
      buildingRender,
      lighting: {
        artificialLight: lighting.artificialLight,
        windowSurfaces: buildingRender.roles.glass,
        windowEmissionIntensity: this.#buildingModels?.windowEmissionIntensity ?? 0,
        streetLights: lighting.streetLights,
        activeLocalLights: lighting.activeLocalLights,
        shadowCastingStreetLights: lighting.shadowCastingStreetLights,
        activeHeadlightSources: vehicleSnapshot.activeHeadlightSources,
        shadowCastingHeadlightSources: vehicleSnapshot.shadowCastingHeadlightSources,
        trafficSignals: lighting.trafficSignals,
        signalAspects: { ...lighting.signalAspects },
        batches: lighting.batches,
      },
      cars: vehicleSnapshot.cars,
      trafficLanes,
      driving: vehicleSnapshot.driving,
      nearbyCarId:
        this.player.controlMode === "on-foot" && !this.#interior && !this.#aboard
          ? this.vehicles.nearestHijackable(position, 6)
          : null,
      vehicleEvent: vehicleSnapshot.vehicleEvent,
      navigation: this.#buildNavigationSnapshot(vehicleSnapshot.cars),
    };
  }
  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#stagingMapTiles?.controller.abort();
    this.#retirementAbortController.abort();
    this.#mapTileLoader.dispose();
    cancelAnimationFrame(this.#raf);
    const staging = this.#stagingMapTiles;
    if (staging) {
      for (const prepared of staging.prepared.values()) {
        if (prepared.lighting) this.lighting.disposePreparedTile(prepared.lighting);
        this.vegetation.disposePreparedTile(prepared.vegetation);
        for (const collider of prepared.colliders) this.physics.removeCollider(collider, false);
        disposeObject(prepared.root, this.#buildingModels?.materials);
      }
      staging.prepared.clear();
      staging.pendingLoads.clear();
      this.#stagingMapTiles = undefined;
    }
    for (const retired of this.#retiredMapTiles) {
      for (const collider of retired.colliders) this.physics.removeCollider(collider, false);
      if (retired.lighting) this.lighting.disposeRetiredTile(retired.lighting);
      if (retired.vegetation) this.vegetation.disposeRetiredTile(retired.vegetation);
      disposeObject(retired.root, this.#buildingModels?.materials);
    }
    this.#retiredMapTiles.length = 0;
    for (const [id, tile] of this.#loadedMapTiles) {
      tile.root.removeFromParent();
      for (const collider of tile.colliders) this.physics.removeCollider(collider, false);
      const retiredLighting = this.lighting.retireTile(id);
      const retiredVegetation = this.vegetation.retireTile(id);
      if (retiredLighting) this.lighting.disposeRetiredTile(retiredLighting);
      if (retiredVegetation) this.vegetation.disposeRetiredTile(retiredVegetation);
      disposeObject(tile.root, this.#buildingModels?.materials);
    }
    this.#loadedMapTiles.clear();
    this.lighting.dispose();
    window.removeEventListener("resize", this.#resize);
    document.removeEventListener("visibilitychange", this.#visibility);
    for (const system of this.systems) system.dispose();
    this.vehicles.dispose();
    this.player.dispose();
    this.streamer.dispose();
    this.navigationWorker.terminate();
    this.#sky?.dispose();
    disposeObject(this.scene, this.#buildingModels?.materials);
    this.#buildingModels?.dispose();
    this.#buildingModels = undefined;
    if (this.#textures) {
      disposeCityTextures(this.#textures);
      this.#textures = undefined;
    }
    this.physics.free();
    this.renderer.dispose();
  }
}
